import { HelpTopic } from 'shared';

/** The languages with CrownDesk manuals (docs/help/crowndesk/<language>), served next to the app. */
const HELP_LANGUAGES = ['nl', 'fr'];

/** Where the manuals in the user's language are; Dutch for any other language. */
export function helpBasePath(language: string | null | undefined): string {
  return `/help/${HELP_LANGUAGES.includes(language ?? '') ? language : 'nl'}`;
}

/** The manuals, with their Dutch titles; the help shows the title of the manual in the user's language once loaded. */
export const HELP_TOPICS: HelpTopic[] = [
  { article: 'werken-met-crowndesk.md', title: 'Werken met CrownDesk – de basis' },
  { article: 'dashboard.md', title: 'Dashboard' },
  { article: 'documenten.md', title: 'Documenten' },
  { article: 'producten.md', title: 'Producten' },
  { article: 'tools.md', title: 'Mijn tools' },
  { article: 'bedrijf.md', title: 'Mijn bedrijf' },
  { article: 'cijfers.md', title: 'Mijn cijfers' },
  { article: 'nieuwsbrieven.md', title: 'Nieuwsbrieven' },
  { article: 'lopende-orders.md', title: 'Lopende orders van je klanten (voor leveranciers)' },
];

/** The first that matches the url (without query) gives the manual, and the section, of a screen. */
const HELP_ARTICLES: [RegExp, string][] = [
  [/^\/dashboard/, 'dashboard.md'],
  [/^\/company_revenue/, 'dashboard.md#omzet'],
  [/^\/sales_data/, 'cijfers.md#verkoopdata'],
  [/^\/stock_sales_reports\/new\//, 'cijfers.md#een-stock--sales-report-versturen'],
  [/^\/stock_sales_reports\/[^/]+/, 'cijfers.md#een-verstuurd-rapport-bekijken'],
  [/^\/stock_sales_reports/, 'cijfers.md#het-stock--sales-report'],
  [/^\/newsletters/, 'nieuwsbrieven.md'],
  [/^\/backorders/, 'lopende-orders.md'],
  [/^\/users\/settings/, 'werken-met-crowndesk.md#je-gebruikersinstellingen'],

  [/^\/documents\/client_orders\/[^/]+/, 'documenten.md#een-order-bekijken'],
  [/^\/documents\/client_orders/, 'documenten.md#orders'],
  [/^\/documents\/invoices\/[^/]+/, 'documenten.md#een-factuur-bekijken'],
  [/^\/documents\/invoices/, 'documenten.md#facturen'],
  [/^\/documents\/credit-notes\/[^/]+/, 'documenten.md#een-creditnota-bekijken'],
  [/^\/documents\/credit-notes/, 'documenten.md#creditnotas'],
  [/^\/documents\/client_returns\/[^/]+/, 'documenten.md#een-retour-bekijken'],
  [/^\/documents\/client_returns/, 'documenten.md#retours'],
  [/^\/documents\/complaints\/new/, 'documenten.md#een-klacht-indienen'],
  [/^\/documents\/complaints\/[^/]+\/messages/, 'documenten.md#berichten-over-een-klacht'],
  [/^\/documents\/complaints\/[^/]+\/attachments/, 'documenten.md#bijlagen-toevoegen-aan-een-klacht'],
  [/^\/documents\/complaints\/[^/]+/, 'documenten.md#een-klacht-opvolgen'],
  [/^\/documents\/complaints/, 'documenten.md#klachten'],
  [/^\/documents/, 'documenten.md'],

  [/^\/products\/articles\/[^/]+/, 'producten.md#een-artikel-bekijken'],
  [/^\/products\/articles/, 'producten.md#artikelen-zoeken-in-de-artikellijst'],
  [/^\/products\/suppliers\/[^/]+/, 'producten.md#een-leverancier-bekijken'],
  [/^\/products\/suppliers/, 'producten.md#leveranciers-opzoeken'],
  [/^\/products\/purchase_conditions/, 'producten.md#aankoopvoorwaarden-bekijken'],
  [/^\/products\/price_adjustments\/[^/]+/, 'producten.md#een-prijsaanpassing-bekijken'],
  [/^\/products\/price_adjustments/, 'producten.md#prijsaanpassingen'],
  [/^\/products/, 'producten.md'],

  [/^\/tools\/carts\/[^/]+\/order_received/, 'tools.md#order-ontvangen'],
  [/^\/tools\/carts\/[^/]+\/articles/, 'tools.md#producten-toevoegen-aan-je-winkelmandje'],
  [/^\/tools\/carts\/[^/]+/, 'tools.md#je-huidige-winkelmandje-afwerken'],
  [/^\/tools\/carts/, 'tools.md#winkelmandjes-het-overzicht'],
  [/^\/tools\/consumer_quotes\/clients/, 'tools.md#klanten-voor-offertes-beheren'],
  [/^\/tools\/consumer_quotes\/settings/, 'tools.md#instellingen-voor-offertes'],
  [/^\/tools\/consumer_quotes\/\d+\/articles/, 'tools.md#producten-en-vrije-regels-in-een-offerte'],
  [/^\/tools\/consumer_quotes\/\d+/, 'tools.md#een-offerte-maken'],
  [/^\/tools\/consumer_quotes/, 'tools.md#offertes-voor-consumenten-het-overzicht'],
  [/^\/tools\/document_library_folders/, 'tools.md#intranet-de-documentbibliotheek'],
  [/^\/tools/, 'tools.md'],

  [/^\/clients\/employees/, 'bedrijf.md#de-werknemers-van-je-bedrijf-bekijken'],
  [/^\/clients\/delivery_addresses/, 'bedrijf.md#de-leveradressen-van-je-bedrijf-bekijken'],
  [/^\/clients\/showrooms/, 'bedrijf.md#de-toonzalen-van-je-bedrijf-bekijken'],
  [/^\/clients\/mailing_lists/, 'bedrijf.md#je-inschrijven-op-een-mailinglijst'],
  [/^\/clients/, 'bedrijf.md#de-gegevens-van-je-bedrijf-bekijken'],
];

/** The manual of the screen at `url`, or the basics when there is none. */
export function helpArticle(url: string): string {
  const path = url.split(/[?#]/)[0];
  return HELP_ARTICLES.find(([pattern]) => pattern.test(path))?.[1] ?? 'werken-met-crowndesk.md';
}
